// src/pages/dashboard/branch/settings/components/BrandSettings.tsx
import { useTranslation } from "react-i18next";
import Card from "../../../../../components/ui/Card";
import InputField from "../../../../../components/ui/InputField";
import Button from "../../../../../components/ui/Button";
import { Branch } from "../../../../../utils/supabaseClient";
import { ArTranslation, LocationType, SocialMediaType } from "../../../../../types/custom";
import { FiFacebook, FiImage, FiInstagram, FiMail, FiMapPin, FiPhone, FiTrash2, FiTwitter, FiUpload, FiGlobe, FiPlus, FiMinus } from "react-icons/fi";
import { useEffect, useMemo, useRef, useState } from "react";
import { uploadToSupabaseStorage } from "../../../../../utils/supabaseStorage";
import GoogleMapsPicker from "../../../../../components/ui/GoogleMapsPicker";

type BrandSettingsProps = {
	restaurant: Branch | null;
	updateRestaurant: (updates: Partial<Branch>) => void;
	updateLocation: (newLocation: Partial<{
		google_maps_link: string;
		coordinates: string;
		place_id: string;
	}>) => void,
	branchId: string | undefined
}

type SocialPlatform = "facebook" | "instagram" | "twitter";

const BrandSettings: React.FC<BrandSettingsProps> = ({ restaurant, updateRestaurant, updateLocation, branchId }) => {
	const { t, i18n } = useTranslation();
	const isRTL = i18n.language === 'ar';

	const [uploadProgress, setUploadProgress] = useState<{ logo?: number, cover?: number, defaultItemImage?: number }>({});
	const logoInputRef = useRef<HTMLInputElement>(null);
	const coverInputRef = useRef<HTMLInputElement>(null);
	const defaultItemImageInputRef = useRef<HTMLInputElement>(null);
	const [socialLinks, setSocialLinks] = useState<Record<SocialPlatform, string>>({
		facebook: '',
		instagram: '',
		twitter: ''
	});
	const [socialErrors, setSocialErrors] = useState<Partial<Record<SocialPlatform, string>>>({});
	const [isLogoDragging, setIsLogoDragging] = useState(false);
	const [isCoverDragging, setIsCoverDragging] = useState(false);
	const [isDefaultItemImageDragging, setIsDefaultItemImageDragging] = useState(false);

	// Get Google Maps API key from environment
	const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

	const locationData = restaurant?.location as LocationType | undefined;

	const initialMapLocation = useMemo(() => {
		if (!locationData?.coordinates || typeof locationData.coordinates !== 'string') {
			return undefined;
		}

		const [latString, lngString] = locationData.coordinates.split(',').map(value => value.trim());
		const lat = Number(latString);
		const lng = Number(lngString);

		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			return undefined;
		}

		const parsedLocation: { lat: number; lng: number; place_id?: string; address?: string } = { lat, lng };

		if (typeof locationData.place_id === 'string') {
			const trimmedPlaceId = locationData.place_id.trim();
			if (trimmedPlaceId) {
				parsedLocation.place_id = trimmedPlaceId;
			}
		}

		const potentialAddress = (locationData as Record<string, unknown>).address;
		if (typeof potentialAddress === 'string' && potentialAddress.trim()) {
			parsedLocation.address = potentialAddress.trim();
		}

		return parsedLocation;
	}, [locationData]);

	useEffect(() => {
		const social = (restaurant?.social_media as SocialMediaType) || {};
		setSocialLinks({
			facebook: typeof social.facebook === 'string' ? social.facebook.trim() : '',
			instagram: typeof social.instagram === 'string' ? social.instagram.trim() : '',
			twitter: typeof social.twitter === 'string' ? social.twitter.trim() : ''
		});
		setSocialErrors({});
	}, [restaurant?.social_media, restaurant]);

	const toNullableUrl = (value: unknown): string | null => {
		if (typeof value !== 'string') {
			return null;
		}
		const trimmed = value.trim();
		return trimmed ? trimmed : null;
	};

	const validateSocialUrl = (platform: SocialPlatform, value: string) => {
		const trimmed = value.trim();
		if (!trimmed) {
			setSocialErrors(prev => ({ ...prev, [platform]: undefined }));
			return true;
		}

		try {
			const url = new URL(trimmed);
			if (!['http:', 'https:'].includes(url.protocol)) {
				throw new Error('Invalid protocol');
			}
			setSocialErrors(prev => ({ ...prev, [platform]: undefined }));
			return true;
		} catch (error) {
			setSocialErrors(prev => ({ ...prev, [platform]: t('settings.errors.invalidUrl') }));
			return false;
		}
	};

	const handleSocialChange = (platform: SocialPlatform, value: string) => {
		setSocialLinks(prev => ({ ...prev, [platform]: value }));
		setSocialErrors(prev => ({ ...prev, [platform]: undefined }));
	};

	const handleSocialBlur = (platform: SocialPlatform) => {
		const currentValue = socialLinks[platform];
		if (validateSocialUrl(platform, currentValue)) {
			const currentSocial = (restaurant?.social_media as SocialMediaType) || {};
			const currentValues = {
				facebook: toNullableUrl(currentSocial.facebook),
				instagram: toNullableUrl(currentSocial.instagram),
				twitter: toNullableUrl(currentSocial.twitter),
				whatsapp: toNullableUrl(currentSocial.whatsapp)
			};
			const nextSocial = {
				facebook: toNullableUrl(socialLinks.facebook),
				instagram: toNullableUrl(socialLinks.instagram),
				twitter: toNullableUrl(socialLinks.twitter),
				whatsapp: currentValues.whatsapp
			};
			const hasChanged =
				nextSocial.facebook !== currentValues.facebook ||
				nextSocial.instagram !== currentValues.instagram ||
				nextSocial.twitter !== currentValues.twitter;

			if (hasChanged) {
				updateRestaurant({ social_media: nextSocial });
			}
		} else {
			const currentSocial = (restaurant?.social_media as SocialMediaType) || {};
			const fallback = toNullableUrl(currentSocial[platform]) ?? '';
			setSocialLinks(prev => ({ ...prev, [platform]: fallback }));
		}
	};

	// Remove image handlers
	const removeImage = (type: 'logo' | 'cover' | 'defaultItemImage') => {
		if (type === 'logo') {
			updateRestaurant({ logo: '' });
		} else if (type === 'cover') {
			updateRestaurant({ cover_image: '' });
		} else {
			updateRestaurant({ default_item_image: '' });
		}
	};

	// File upload handlers
	const handleFileUpload = async (file: File, type: 'logo' | 'cover' | 'defaultItemImage') => {
		if (!file || !branchId) return;

		try {
			setUploadProgress(prev => ({ ...prev, [type]: 0 }));

			// Validate file type
			const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
			if (!validTypes.includes(file.type)) {
				throw new Error(t('settings.errors.invalidFileType'));
			}

			// Validate file size (max 5MB)
			if (file.size > 5 * 1024 * 1024) {
				throw new Error(t('settings.errors.fileTooLarge'));
			}

			// Generate unique filename
			const fileName = `${type}-${Date.now()}.${file.name.split('.').pop()}`;

			// Upload to Supabase Storage
			const result = await uploadToSupabaseStorage(file, fileName, {
				bucket: 'uploads',
				path: `${type}s/${restaurant?.organization_id}`,
				upsert: true
			});

			// Update restaurant state
			if (type === 'logo') {
				updateRestaurant({ logo: result.publicUrl });
			} else if (type === 'cover') {
				updateRestaurant({ cover_image: result.publicUrl });
			} else {
				updateRestaurant({ default_item_image: result.publicUrl });
			}

			setUploadProgress(prev => ({ ...prev, [type]: 100 }));

			// Clear progress after 2 seconds
			setTimeout(() => {
				setUploadProgress(prev => ({ ...prev, [type]: undefined }));
			}, 2000);

		} catch (error) {
			console.error(`Error uploading ${type}:`, error);
			setUploadProgress(prev => ({ ...prev, [type]: undefined }));
			// Handle error (show toast notification)
		}
	};

	// Helper to update Arabic translations
	const updateArabicTranslations = (field: 'name' | 'description' | 'address', value: string | string[]) => {
		const currentAr = (restaurant?.ar as ArTranslation) || { name: '', description: '', address: [] };
		updateRestaurant({
			ar: {
				...currentAr,
				[field]: field === 'address' ? (Array.isArray(value) ? value : [value]) : value
			}
		});
	};

	const getAlignedAddresses = () => {
		const englishSource = Array.isArray(restaurant?.address)
			? restaurant.address
			: restaurant?.address
				? [restaurant.address]
				: [];

		const arTranslation = restaurant?.ar as ArTranslation;
		const arabicSource = Array.isArray(arTranslation?.address)
			? arTranslation.address
			: arTranslation?.address
				? [arTranslation.address]
				: [];

		const maxLength = Math.max(englishSource.length, arabicSource.length, 1);

		const english = [...englishSource];
		const arabic = [...arabicSource];

		while (english.length < maxLength) {
			english.push('');
		}

		while (arabic.length < maxLength) {
			arabic.push('');
		}

		return { english, arabic };
	};

	// Helper functions for managing addresses (both English and Arabic)
	const addAddress = () => {
		const { english, arabic } = getAlignedAddresses();
		english.push('');
		arabic.push('');
		updateRestaurant({ address: english });
		updateArabicTranslations('address', arabic);
	};

	const updateEnglishAddress = (index: number, value: string) => {
		const { english, arabic } = getAlignedAddresses();
		if (index >= english.length) {
			while (english.length <= index) {
				english.push('');
			}
		}
		english[index] = value;
		updateRestaurant({ address: english });
		updateArabicTranslations('address', arabic);
	};

	const updateArabicAddress = (index: number, value: string) => {
		const { english, arabic } = getAlignedAddresses();
		if (index >= arabic.length) {
			while (arabic.length <= index) {
				arabic.push('');
			}
		}
		arabic[index] = value;
		updateRestaurant({ address: english });
		updateArabicTranslations('address', arabic);
	};

	const removeAddress = (index: number) => {
		const { english, arabic } = getAlignedAddresses();
		const updatedEnglish = english.filter((_, i) => i !== index);
		const updatedArabic = arabic.filter((_, i) => i !== index);

		if (!updatedEnglish.length) {
			updatedEnglish.push('');
		}

		if (!updatedArabic.length) {
			updatedArabic.push('');
		}

		updateRestaurant({ address: updatedEnglish });
		updateArabicTranslations('address', updatedArabic);
	};

	// Handle location selection from map picker
	const handleLocationSelect = (location: { lat: number; lng: number; address?: string; place_id?: string }) => {
		const coordinates = `${location.lat}, ${location.lng}`;
		const googleMapsLink = `https://maps.google.com/?q=${location.lat},${location.lng}`;

		// Update all location properties in a single call
		const locationUpdate: { coordinates: string; google_maps_link: string; place_id?: string } = {
			coordinates,
			google_maps_link: googleMapsLink,
			place_id: location.place_id
		};

		if (location.place_id) {
			locationUpdate.place_id = location.place_id;
		}

		updateLocation(locationUpdate);

		// Don't auto-fill address from map - the geocoded address from Google Maps
		// is often inaccurate or formatted differently than what the user wants.
		// Let users manually enter their preferred address format.
	};

	// Parse current coordinates for initial map location
	const getCurrentLocation = () => {
		const coordinates = (restaurant?.location as LocationType)?.coordinates;
		if (coordinates && typeof coordinates === 'string') {
			const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()));
			if (!isNaN(lat) && !isNaN(lng)) {
				return { lat, lng };
			}
		}
		return undefined;
	};

	const { english: englishAddresses, arabic: arabicAddresses } = getAlignedAddresses();

	return <div className="space-y-6">
		{/* Restaurant Information - English & Arabic */}
		<Card>
			<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
				{t('settings.restaurantInfo')}
			</h3>
			<div className="space-y-6">
				{/* Restaurant Name - Bilingual */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					<InputField
						label={t('settings.restaurantName')}
						value={restaurant?.name ?? ''}
						onChange={(e) => updateRestaurant({ name: e.target.value })}
						placeholder={t('settings.restaurantNamePlaceholder')}
						dir="ltr"
					/>
					<InputField
						label={t('settings.restaurantNameArabic')}
						value={(restaurant?.ar as ArTranslation)?.name ?? ''}
						onChange={(e) => updateArabicTranslations('name', e.target.value)}
						placeholder={t('settings.restaurantNameArabicPlaceholder')}
						dir="rtl"
					/>
				</div>

				{/* Description - Bilingual */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							{t('settings.description')}
						</label>
						<textarea
							value={restaurant?.description ?? ''}
							onChange={(e) => updateRestaurant({ description: e.target.value })}
							placeholder={t('settings.descriptionPlaceholder')}
							rows={3}
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
							dir="ltr"
						/>
					</div>
					<div>
						<label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
							{t('settings.descriptionArabic')}
						</label>
						<textarea
							value={(restaurant?.ar as ArTranslation)?.description ?? ''}
							onChange={(e) => updateArabicTranslations('description', e.target.value)}
							placeholder={t('settings.descriptionArabicPlaceholder')}
							rows={3}
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
							dir="rtl"
						/>
					</div>
				</div>

				{/* Contact Information */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					<InputField
						label={t('settings.phone')}
						value={restaurant?.phone ?? ''}
						onChange={(e) => updateRestaurant({ phone: e.target.value })}
						placeholder={t('settings.phonePlaceholder')}
						icon={<FiPhone />}
						dir="ltr"
					/>
					<InputField
						label={t('settings.email')}
						type="email"
						value={restaurant?.email ?? ''}
						onChange={(e) => updateRestaurant({ email: e.target.value })}
						placeholder={t('settings.emailPlaceholder')}
						icon={<FiMail />}
						dir="ltr"
					/>
				</div>

				{/* Address - Bilingual */}
				<div>
					<div className="flex items-center justify-between mb-3">
						<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							{t('settings.address')}
						</label>
						<button
							onClick={addAddress}
							className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
						>
							<FiPlus className="w-3 h-3" />
							{t('settings.addAddress')}
						</button>
					</div>

					<div className="space-y-3">
						{englishAddresses.map((englishAddress, index) => {
							const labelSuffix = englishAddresses.length > 1 ? ` #${index + 1}` : '';
							const englishPlaceholder = englishAddresses.length > 1
								? `${t('settings.addressPlaceholder')} ${index + 1}`
								: t('settings.addressPlaceholder');
							const arabicPlaceholder = englishAddresses.length > 1
								? `${t('settings.addressArabicPlaceholder')} ${index + 1}`
								: t('settings.addressArabicPlaceholder');
							const arabicValue = arabicAddresses[index] ?? '';

							return (
								<div
									key={`address-row-${index}`}
									className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-start"
								>
									<div>
										<label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
											{t('settings.address')}
											{labelSuffix}
										</label>
										<textarea
											value={englishAddress}
											onChange={(e) => updateEnglishAddress(index, e.target.value)}
											placeholder={englishPlaceholder}
											rows={2}
											className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
											dir="ltr"
										/>
									</div>
									<div>
										<label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-2">
											<FiGlobe className="w-3 h-3" />
											{t('settings.addressArabic')}
											{labelSuffix}
										</label>
										<textarea
											value={arabicValue}
											onChange={(e) => updateArabicAddress(index, e.target.value)}
											placeholder={arabicPlaceholder}
											rows={2}
											className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
											dir="rtl"
										/>
									</div>
									{englishAddresses.length > 1 && (
										<button
											onClick={() => removeAddress(index)}
											className="flex items-center justify-center h-10 w-10 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors self-start mt-5 lg:mt-0"
											aria-label={`${t('settings.removeAddress')} ${labelSuffix}`.trim()}
										>
											<FiMinus className="w-4 h-4" />
										</button>
									)}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</Card>

		{/* Brand Images */}
		<Card>
			<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
				{t('settings.brandImages')}
			</h3>
			<div className="space-y-6">
				{/* Logo Upload */}
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.logo')}
					</label>

					{restaurant?.logo ? (
						<div className="relative">
							<div className="flex items-center gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
								<img
									src={restaurant?.logo}
									alt="Logo"
									className="w-16 h-16 object-contain rounded-lg"
								/>
								<div className="flex-1">
									<p className="text-sm font-medium text-gray-900 dark:text-white">
										{t('settings.logoUploaded')}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{t('settings.clickToChange')}
									</p>
								</div>
								<button
									onClick={() => removeImage('logo')}
									className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg cursor-pointer"
								>
									<FiTrash2 className="h-4 w-4" />
								</button>
							</div>

							<input
								ref={logoInputRef}
								type="file"
								accept="image/*"
								onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'logo')}
								className="-z-10 absolute inset-0 w-full h-full opacity-0 cursor-pointer"
								onDragOver={(e) => {
									e.preventDefault();
									setIsLogoDragging(true);
								}}
								onDragLeave={(e) => {
									e.preventDefault();
									setIsLogoDragging(false);
								}}
								onDrop={(e) => {
									e.preventDefault();
									setIsLogoDragging(false);
									const files = e.dataTransfer.files;
									if (files.length > 0) {
										handleFileUpload(files[0], 'logo');
									}
								}}
							/>
						</div>
					) : (
						<div
							onClick={() => logoInputRef.current?.click()}
							className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-primary cursor-pointer transition-colors ${isLogoDragging ? 'border-primary bg-primary/5' : 'border-gray-300 dark:border-gray-600'}`}
							onDragOver={(e) => {
								e.preventDefault();
								setIsLogoDragging(true);
							}}
							onDragLeave={(e) => {
								e.preventDefault();
								setIsLogoDragging(false);
							}}
							onDrop={(e) => {
								e.preventDefault();
								setIsLogoDragging(false);
								const files = e.dataTransfer.files;
								if (files.length > 0) {
									handleFileUpload(files[0], 'logo');
								}
							}}
						>
							<FiUpload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
							<p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
								{t('settings.uploadLogo')}
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								{t('settings.supportedFormats')}
							</p>
							<input
								ref={logoInputRef}
								type="file"
								accept="image/*"
								onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'logo')}
								className="hidden"
							/>
						</div>
					)}

					{uploadProgress.logo !== undefined && uploadProgress.logo < 100 && (
						<div className="mt-2">
							<div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
								<div
									className="bg-primary h-2 rounded-full transition-all duration-300"
									style={{ width: `${uploadProgress.logo}%` }}
								/>
							</div>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								{t('settings.uploading')} {uploadProgress.logo}%
							</p>
						</div>
					)}
				</div>

				{/* Cover Image Upload */}
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.coverImage')}
					</label>

					{restaurant?.cover_image ? (
						<div className="relative">
							<div className="flex items-center gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
								<img
									src={restaurant?.cover_image}
									alt="Cover"
									className="w-24 h-16 object-cover rounded-lg"
								/>
								<div className="flex-1">
									<p className="text-sm font-medium text-gray-900 dark:text-white">
										{t('settings.coverImageUploaded')}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{t('settings.clickToChange')}
									</p>
								</div>
								<button
									onClick={() => removeImage('cover')}
									className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg cursor-pointer"
								>
									<FiTrash2 className="h-4 w-4" />
								</button>
							</div>

							<input
								ref={coverInputRef}
								type="file"
								accept="image/*"
								onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'cover')}
								className="-z-10 absolute inset-0 w-full h-full opacity-0 cursor-pointer"
								onDragOver={(e) => {
									e.preventDefault();
									setIsCoverDragging(true);
								}}
								onDragLeave={(e) => {
									e.preventDefault();
									setIsCoverDragging(false);
								}}
								onDrop={(e) => {
									e.preventDefault();
									setIsCoverDragging(false);
									const files = e.dataTransfer.files;
									if (files.length > 0) {
										handleFileUpload(files[0], 'cover');
									}
								}}
							/>
						</div>
					) : (
						<div
							onClick={() => coverInputRef.current?.click()}
							className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-primary cursor-pointer transition-colors ${isCoverDragging ? 'border-primary bg-primary/5' : 'border-gray-300 dark:border-gray-600'}`}
							onDragOver={(e) => {
								e.preventDefault();
								setIsCoverDragging(true);
							}}
							onDragLeave={(e) => {
								e.preventDefault();
								setIsCoverDragging(false);
							}}
							onDrop={(e) => {
								e.preventDefault();
								setIsCoverDragging(false);
								const files = e.dataTransfer.files;
								if (files.length > 0) {
									handleFileUpload(files[0], 'cover');
								}
							}}
						>
							<FiImage className="mx-auto h-12 w-12 text-gray-400 mb-2" />
							<p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
								{t('settings.uploadCoverImage')}
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								{t('settings.supportedFormats')}
							</p>
							<input
								ref={coverInputRef}
								type="file"
								accept="image/*"
								onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'cover')}
								className="hidden"
							/>
						</div>
					)}

					{uploadProgress.cover !== undefined && uploadProgress.cover < 100 && (
						<div className="mt-2">
							<div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
								<div
									className="bg-primary h-2 rounded-full transition-all duration-300"
									style={{ width: `${uploadProgress.cover}%` }}
								/>
							</div>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								{t('settings.uploading')} {uploadProgress.cover}%
							</p>
						</div>
					)}
				</div>

				{/* Default Item Image Upload */}
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.defaultItemImage')}
					</label>

					{restaurant?.default_item_image ? (
						<div className="relative">
							<div className="flex items-center gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
								<img
									src={restaurant?.default_item_image}
									alt="Cover"
									className="w-24 h-16 object-cover rounded-lg"
								/>
								<div className="flex-1">
									<p className="text-sm font-medium text-gray-900 dark:text-white">
										{t('settings.defaultItemImageUploaded')}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{t('settings.clickToChange')}
									</p>
								</div>
								<button
									onClick={() => removeImage('defaultItemImage')}
									className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg cursor-pointer"
								>
									<FiTrash2 className="h-4 w-4" />
								</button>
							</div>

							<input
								ref={defaultItemImageInputRef}
								type="file"
								accept="image/*"
								onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'defaultItemImage')}
								className="-z-10 absolute inset-0 w-full h-full opacity-0 cursor-pointer"
								onDragOver={(e) => {
									e.preventDefault();
									setIsDefaultItemImageDragging(true);
								}}
								onDragLeave={(e) => {
									e.preventDefault();
									setIsDefaultItemImageDragging(false);
								}}
								onDrop={(e) => {
									e.preventDefault();
									setIsDefaultItemImageDragging(false);
									const files = e.dataTransfer.files;
									if (files.length > 0) {
										handleFileUpload(files[0], 'defaultItemImage');
									}
								}}
							/>
						</div>
					) : (
						<div
							onClick={() => defaultItemImageInputRef.current?.click()}
							className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-primary cursor-pointer transition-colors ${isDefaultItemImageDragging ? 'border-primary bg-primary/5' : 'border-gray-300 dark:border-gray-600'}`}
							onDragOver={(e) => {
								e.preventDefault();
								setIsDefaultItemImageDragging(true);
							}}
							onDragLeave={(e) => {
								e.preventDefault();
								setIsDefaultItemImageDragging(false);
							}}
							onDrop={(e) => {
								e.preventDefault();
								setIsDefaultItemImageDragging(false);
								const files = e.dataTransfer.files;
								if (files.length > 0) {
									handleFileUpload(files[0], 'defaultItemImage');
								}
							}}
						>
							<FiImage className="mx-auto h-12 w-12 text-gray-400 mb-2" />
							<p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
								{t('settings.uploadDefaultItemImage')}
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								{t('settings.supportedFormats')}
							</p>
							<input
								ref={defaultItemImageInputRef}
								type="file"
								accept="image/*"
								onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'defaultItemImage')}
								className="hidden"
							/>
						</div>
					)}

					{uploadProgress.defaultItemImage !== undefined && uploadProgress.defaultItemImage < 100 && (
						<div className="mt-2">
							<div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
								<div
									className="bg-primary h-2 rounded-full transition-all duration-300"
									style={{ width: `${uploadProgress.defaultItemImage}%` }}
								/>
							</div>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								{t('settings.uploading')} {uploadProgress.defaultItemImage}%
							</p>
						</div>
					)}
				</div>
			</div>
		</Card>

		{/* Location Settings */}
		<Card>
			<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
				{t('settings.location')}
			</h3>
			<div className="space-y-4">
				<InputField
					label={t('settings.googleMapsLink')}
					value={(restaurant?.location as LocationType)?.google_maps_link ?? ''}
					onChange={(e) => updateLocation({ google_maps_link: e.target.value })}
					placeholder={t('settings.googleMapsLinkPlaceholder')}
					icon={<FiMapPin />}
					dir="ltr"
				/>

				<div className="text-center py-2">
					<span className="text-sm text-gray-500 dark:text-gray-400">
						{t('settings.or')}
					</span>
				</div>

				<InputField
					label={t('settings.coordinates')}
					value={(restaurant?.location as LocationType)?.coordinates ?? ''}
					onChange={(e) => updateLocation({ coordinates: e.target.value })}
					placeholder={t('settings.coordinatesPlaceholder')}
					icon={<FiMapPin />}
					dir="ltr"
				/>
				<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
					{t('settings.coordinatesHelper')}
				</p>

				{/* Inline Map Picker */}
				{googleMapsApiKey && (
					<div className="mt-6">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
							{t('settings.selectOnMap')}
						</label>
						<div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
							<GoogleMapsPicker
								isOpen={true}
								onClose={() => { }} // No close needed for inline
								onLocationSelect={handleLocationSelect}
								initialLocation={initialMapLocation}
								apiKey={googleMapsApiKey}
								inline={true}
							/>
						</div>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
							{t('settings.dragMarker')}
						</p>
					</div>
				)}
			</div>
		</Card>

		{/* Social Media */}
		<Card>
			<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
				{t('settings.socialMedia')}
			</h3>
			<div className="space-y-4">
				<InputField
					label={t('settings.facebook')}
					value={socialLinks.facebook}
					onChange={(e) => handleSocialChange('facebook', e.target.value)}
					onBlur={() => handleSocialBlur('facebook')}
					placeholder="https://facebook.com/yourrestaurant"
					icon={<FiFacebook />}
					dir="ltr"
					error={socialErrors.facebook}
				/>

				<InputField
					label={t('settings.instagram')}
					value={socialLinks.instagram}
					onChange={(e) => handleSocialChange('instagram', e.target.value)}
					onBlur={() => handleSocialBlur('instagram')}
					placeholder="https://instagram.com/yourrestaurant"
					icon={<FiInstagram />}
					dir="ltr"
					error={socialErrors.instagram}
				/>

				<InputField
					label={t('settings.twitter')}
					value={socialLinks.twitter}
					onChange={(e) => handleSocialChange('twitter', e.target.value)}
					onBlur={() => handleSocialBlur('twitter')}
					placeholder="https://twitter.com/yourrestaurant"
					icon={<FiTwitter />}
					dir="ltr"
					error={socialErrors.twitter}
				/>
			</div>
		</Card>

		{/* Features */}
		{/* <FeatureSettings /> */}

		{/* Google Maps Picker Modal */}
	</div>
};

export default BrandSettings;